/**
 * Admin authentication utilities
 * Handles secure token storage and verification
 */

import API_URL from '../config/api';

/**
 * Get admin token from sessionStorage
 */
export function getAdminToken() {
  return sessionStorage.getItem('adminToken');
}

/**
 * Set admin token in sessionStorage
 */
export function setAdminToken(token) {
  sessionStorage.setItem('adminToken', token);
}

/**
 * Remove admin token from sessionStorage
 */
export function removeAdminToken() {
  sessionStorage.removeItem('adminToken');
}

function getStoredUser() {
  const storedUser = localStorage.getItem('nightfall_user');
  if (!storedUser) {
    return null;
  }
  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
}

function getUserAuthHeader() {
  const user = getStoredUser();
  const token = localStorage.getItem('nightfall_token');
  if (user?.role === 'admin' && token) {
    return `Bearer ${token}`;
  }
  return null;
}

/**
 * Check if admin is authenticated (localStorage/sessionStorage only).
 * Prefer useAdminAuth().hasAdminAccess in React components.
 */
export function isAdminAuthenticated() {
  if (getAdminToken()) {
    return true;
  }
  const user = getStoredUser();
  return user?.role === 'admin';
}

/**
 * Get authorization header for admin API calls.
 * Prefer useAdminAuth().getAuthHeader() in React components.
 */
export function getAdminAuthHeader() {
  const adminToken = getAdminToken();
  if (adminToken) {
    return `Bearer ${adminToken}`;
  }
  return getUserAuthHeader();
}

/**
 * Verify admin access with the backend using a specific auth header.
 */
export async function verifyAdminAccess(authHeader) {
  const header = authHeader || getAdminAuthHeader();
  if (!header) {
    return { valid: false, error: 'No token' };
  }

  try {
    const response = await fetch(`${API_URL}/admin/verify`, {
      method: 'GET',
      headers: {
        Authorization: header,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      return { valid: true };
    }

    const legacyToken = getAdminToken();
    if (legacyToken && header.includes(legacyToken)) {
      removeAdminToken();
    }

    return { valid: false, error: 'Token invalid or expired', status: response.status };
  } catch (error) {
    console.error('Admin token verification error:', error);
    return { valid: false, error: 'Verification failed' };
  }
}

/**
 * Verify admin token with backend
 */
export async function verifyAdminToken() {
  return verifyAdminAccess(getAdminAuthHeader());
}

